import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { formatAmount } from '@/lib/utils'
import type { TrendData } from '@/lib/hooks'

type Props = {
  trendData: TrendData[]
  currentMonth: string
}

type TooltipPayloadEntry = {
  value?: number
  payload?: TrendData
}

function TrendTooltip({ active, payload }: { active?: boolean; payload?: TooltipPayloadEntry[] }) {
  if (!active || !payload || payload.length === 0) return null
  const point = payload[0].payload
  if (!point) return null
  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md">
      <div className="text-muted-foreground">{point.label}</div>
      <div className="mt-1 font-medium tabular-nums">{formatAmount(point.total)}</div>
    </div>
  )
}

export function MonthlyTrendCard({ trendData, currentMonth }: Props) {
  if (trendData.length === 0) return null

  // Average over the months *excluding* the current one — this is the
  // baseline the user is being compared against.
  const past = trendData.filter((t) => t.month !== currentMonth)
  const baselineAvg =
    past.length > 0 ? past.reduce((sum, t) => sum + t.total, 0) / past.length : 0

  const fmtTick = (value: number) =>
    value >= 10000 ? `${Math.round(value / 10000)}万` : value.toLocaleString()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">直近6ヶ月の推移</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={trendData} margin={{ top: 5, right: 8, left: -8, bottom: 0 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                tickFormatter={fmtTick}
                width={42}
              />
              <Tooltip content={<TrendTooltip />} cursor={{ fill: 'currentColor', opacity: 0.06 }} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                {trendData.map((d) => (
                  <Cell
                    key={d.month}
                    fill={d.month === currentMonth ? 'var(--primary)' : 'color-mix(in oklab, var(--muted-foreground) 40%, transparent)'}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {baselineAvg > 0 && (
          <div className="mt-2 text-xs text-muted-foreground tabular-nums">
            5ヶ月平均 {formatAmount(Math.round(baselineAvg), 'JPY', true)}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
